import React, { useMemo, useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, FileDown, LogIn, ShieldCheck } from "lucide-react";
import { useAuth, loginWithGoogle } from "../lib/auth";
import { saveDraft } from "../lib/draft";
import PdfPreviewModal from "../components/PdfPreviewModal";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Paramètres fixes du dispositif BAR-TH-168 ----------
const PARAMS = {
  unitM2: 2,
  paliersECS: { 2: 8, 4: 12, 6: 16 },
  forfait: {
    H1: { ECS: 6000, CH: 14000 },
    H2: { ECS: 7200, CH: 12700 },
    H3: { ECS: 9600, CH: 10300 },
  },
};

// ---------- Helpers d'affichage ----------
const fmt = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(n);
const fmt0 = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n);
const euro = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);

const nFromShab = (shab, ratio) => Math.max(1, Math.round(shab * ratio));

function bonus(appoint, menage, dateStr) {
  const d = dateStr ? new Date(dateStr + "T00:00:00") : null;
  const win =
    d && d >= new Date(2026, 8, 1) && d <= new Date(2026, 11, 31);
  const fossile = appoint === "gaz" || appoint === "fioul";
  if (!win) return { k: 1, why: "Hors fenêtre bonus" };
  if (fossile) return { k: 1, why: "Fossile conservé" };
  return menage === "modeste"
    ? { k: 5, why: "×5 II ter" }
    : { k: 4, why: "×4 autre ménage" };
}

function pack(S, u, zone, appoint, menage, dateStr, prix) {
  const minS = u === "CH" ? 8 : 2;
  const elecKo = u === "CH" && appoint === "elec";
  const ok = S >= minS && !elecKo;
  const forfait =
    u === "CH" ? PARAMS.forfait[zone].CH : PARAMS.forfait[zone].ECS;
  const b = bonus(appoint, menage, dateStr);
  const qBase = ok ? S * forfait : 0;
  const qTot = qBase * b.k;
  return { minS, elecKo, ok, forfait, b, qBase, qTot, prime: (qTot / 1000) * prix };
}

// ---------- Composant principal ----------
export default function Bar168Simulator() {
  const { user } = useAuth();

  // Etat du formulaire — deux configurations SSC : chauffage+ECS ou ECS seule
  const [usage, setUsage] = useState("CH"); // 'CH' = SSC Chauffage+ECS, 'ECS' = SSC ECS seule
  const [shab, setShab] = useState(120);
  const [ratio, setRatio] = useState(0.125);
  const [pers, setPers] = useState(4);
  const [np, setNp] = useState(12);
  const [zone, setZone] = useState("H2");
  const [appoint, setAppoint] = useState("pac");
  const [menage, setMenage] = useState("autre");
  const [date, setDate] = useState("2026-09-17");
  const [prix, setPrix] = useState(7);
  const [projectName, setProjectName] = useState("");

  // Affichage du résultat (rendu manuel via bouton "Calculer")
  const [calculated, setCalculated] = useState(false);
  const [flash, setFlash] = useState(false);
  const [saving, setSaving] = useState(false);
  const [authPromptOpen, setAuthPromptOpen] = useState(null); // 'save' | 'export' | null
  const [pdfModal, setPdfModal] = useState({
    open: false,
    previewUrl: null,
    downloadUrl: null,
    filename: null,
  });

  // Sync palier ECS -> panneaux quand on change "pers"
  useEffect(() => {
    setNp(PARAMS.paliersECS[pers]);
  }, [pers]);

  // Toute modification invalide l'affichage précédent
  useEffect(() => {
    setCalculated(false);
  }, [usage, shab, ratio, pers, np, zone, appoint, menage, date, prix]);

  const snapshot = useMemo(() => {
    const unit = PARAMS.unitM2;
    let n = 0,
      pose = "";
    if (usage === "CH") {
      n = nFromShab(Number(shab) || 0, Number(ratio));
      pose =
        fmt0(shab) +
        " m² hab. × " +
        Math.round(ratio * 1000) / 10 +
        " % = " +
        n +
        " panneaux";
    } else {
      n = Number(np) || 0;
      pose = pers + " pers. · palier " + n + " panneaux";
    }
    const S = n * unit;
    const c = pack(S, usage, zone, appoint, menage, date, Number(prix) || 0);
    return {
      unit,
      zone,
      appoint,
      menage,
      dateStr: date,
      prix: Number(prix) || 0,
      n,
      pose,
      S,
      c,
      shab: Number(shab) || 0,
      ratio: Number(ratio),
      pers,
    };
  }, [usage, shab, ratio, pers, np, zone, appoint, menage, date, prix]);

  const runCalc = () => {
    setCalculated(true);
    setFlash(true);
    setTimeout(() => setFlash(false), 700);
  };

  const buildPdfQuery = () => {
    const s = snapshot;
    const params = {
      name: projectName.trim() || "Simulation BAR-TH-168",
      usage,
      zone: s.zone,
      appoint: s.appoint,
      menage: s.menage,
      date_engagement: s.dateStr,
      prix_mwhc: s.prix,
      panneaux: s.n,
      surface_cee_m2: s.S,
      forfait_kwhc_m2: s.c.forfait,
      bonus_k: s.c.b.k,
      bonus_reason: s.c.b.why,
      q_base_mwhc: s.c.qBase / 1000,
      q_total_mwhc: s.c.qTot / 1000,
      prime_eur: s.c.prime,
    };
    if (usage === "CH") {
      params.shab_m2 = s.shab;
      params.ratio_shab = s.ratio;
    } else {
      params.personnes = Number(s.pers);
    }
    return new URLSearchParams(params).toString();
  };

  const handleExportPdf = () => {
    if (!user) {
      setAuthPromptOpen("export");
      return;
    }
    if (!calculated) {
      toast.error("Cliquez d'abord sur « Calculer la prime »");
      return;
    }
    const qs = buildPdfQuery();
    const safe = (projectName.trim() || "simulation").replace(/[^a-z0-9_\-]/gi, "_");
    setPdfModal({
      open: true,
      previewUrl: `${API}/bar168-pdf/preview?${qs}`,
      downloadUrl: `${API}/bar168-pdf?${qs}&download=1`,
      filename: `BAR-TH-168_${safe}.pdf`,
    });
  };

  const handleSave = async () => {
    if (!user) {
      setAuthPromptOpen("save");
      return;
    }
    if (!calculated) {
      toast.error("Cliquez d'abord sur « Calculer la prime »");
      return;
    }
    if (!projectName.trim()) {
      toast.error("Donnez un nom à la simulation avant de sauvegarder");
      return;
    }
    setSaving(true);
    try {
      const s = snapshot;
      await axios.post(
        `${API}/bar168-simulations`,
        {
          name: projectName.trim(),
          usage,
          zone: s.zone,
          appoint: s.appoint,
          menage: s.menage,
          date_engagement: s.dateStr,
          prix_mwhc: s.prix,
          shab_m2: usage === "CH" ? s.shab : null,
          ratio_shab: usage === "CH" ? s.ratio : null,
          personnes: usage === "ECS" ? Number(s.pers) : null,
          panneaux: s.n,
          surface_cee_m2: s.S,
          forfait_kwhc_m2: s.c.forfait,
          bonus_k: s.c.b.k,
          bonus_reason: s.c.b.why,
          q_base_mwhc: s.c.qBase / 1000,
          q_total_mwhc: s.c.qTot / 1000,
          prime_eur: s.c.prime,
        },
        { withCredentials: true }
      );
      toast.success("Simulation enregistrée");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Rendu ----------
  const s = snapshot;
  const flags = [];
  if (s.c.elecKo) flags.push(["bad", "Appoint électrique interdit pour un SSC Chauffage + ECS"]);
  else if (!s.c.ok) flags.push(["bad", "S " + fmt(s.S) + " m² < mini " + s.c.minS + " m²"]);
  else flags.push(["ok", "S " + fmt(s.S) + " m² ≥ " + s.c.minS + " m²"]);
  if (s.appoint === "pac") flags.push(["warn", "Non-cumul 171/172"]);

  const hintCH =
    "10 % = " +
    nFromShab(s.shab, 0.1) +
    " p. · 12,5 % = " +
    nFromShab(s.shab, 0.125) +
    " p. · 15 % = " +
    nFromShab(s.shab, 0.15) +
    " p.";

  return (
    <div className="bar168-root">
      <style>{styles}</style>
      <div className="wrap">
        <header className="hero">
          <div>
            <div className="hero-badge">CEE · BAR-TH-168</div>
            <h1>Simulateur solaire thermique</h1>
            <p>
              <strong>SSC – Système Solaire Combiné.</strong> Deux configurations possibles :
              chauffage + ECS (10 à 15 % de la Shab en panneaux) ou ECS seule (paliers foyer).
              Surface CEE = panneaux × 2 m² (paramètre interne).
            </p>
            <div className="hero-actions">
              <button
                type="button"
                className="btn"
                onClick={handleExportPdf}
                data-testid="bar168-export-pdf-btn"
              >
                <FileDown size={16} /> Exporter en PDF
              </button>
              <button
                type="button"
                className="btn ghost"
                onClick={handleSave}
                disabled={saving}
                data-testid="bar168-save-btn"
              >
                <Save size={16} /> {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </header>

        <div className="modes no-print">
          <button
            type="button"
            className={"mode" + (usage === "CH" ? " on" : "")}
            onClick={() => setUsage("CH")}
            data-testid="bar168-mode-ch"
          >
            <strong>SSC – Chauffage + ECS</strong>
            <span>Panneaux = 10 / 12,5 / 15 % de la Shab.</span>
          </button>
          <button
            type="button"
            className={"mode" + (usage === "ECS" ? " on" : "")}
            onClick={() => setUsage("ECS")}
            data-testid="bar168-mode-ecs"
          >
            <strong>SSC – ECS seule</strong>
            <span>2 / 4 / 6 pers. → 8 / 12 / 16 panneaux.</span>
          </button>
        </div>

        <div className="layout">
          {/* --- Formulaire --- */}
          <form
            className="card"
            onSubmit={(e) => {
              e.preventDefault();
              runCalc();
            }}
          >
            {usage === "CH" ? (
              <div>
                <h2>Pose SSC – Chauffage + ECS</h2>
                <label htmlFor="shab">Surface habitable (m²)</label>
                <input
                  id="shab"
                  type="number"
                  min="30"
                  max="400"
                  value={shab}
                  onChange={(e) => setShab(e.target.value)}
                  data-testid="bar168-shab-input"
                />
                <label htmlFor="ratio">Part de la Shab en panneaux</label>
                <select
                  id="ratio"
                  value={ratio}
                  onChange={(e) => setRatio(Number(e.target.value))}
                  data-testid="bar168-ratio-select"
                >
                  <option value={0.1}>10 %</option>
                  <option value={0.125}>12,5 %</option>
                  <option value={0.15}>15 %</option>
                </select>
                <p className="hint">{hintCH}</p>
              </div>
            ) : (
              <div>
                <h2>Pose SSC – ECS seule</h2>
                <label htmlFor="pers">Personnes</label>
                <select
                  id="pers"
                  value={pers}
                  onChange={(e) => setPers(Number(e.target.value))}
                  data-testid="bar168-pers-select"
                >
                  <option value={2}>2 personnes → 8 panneaux</option>
                  <option value={4}>4 personnes → 12 panneaux</option>
                  <option value={6}>6 personnes → 16 panneaux</option>
                </select>
                <label htmlFor="np">Panneaux (modifiable)</label>
                <input
                  id="np"
                  type="number"
                  min="1"
                  max="40"
                  value={np}
                  onChange={(e) => setNp(e.target.value)}
                  data-testid="bar168-np-input"
                />
              </div>
            )}

            <h2 style={{ marginTop: 14 }}>Dossier CEE</h2>

            <label htmlFor="pname">Nom de la simulation</label>
            <input
              id="pname"
              type="text"
              placeholder="Ex. M. Dupont – Villa Toulouse"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              data-testid="bar168-name-input"
            />

            <label htmlFor="zone">Zone</label>
            <select
              id="zone"
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              data-testid="bar168-zone-select"
            >
              <option value="H1">H1</option>
              <option value="H2">H2</option>
              <option value="H3">H3</option>
            </select>

            <label htmlFor="appoint">Appoint</label>
            <select
              id="appoint"
              value={appoint}
              onChange={(e) => setAppoint(e.target.value)}
              data-testid="bar168-appoint-select"
            >
              <option value="pac">PAC, pas de fossile</option>
              <option value="bois">Bois / biomasse</option>
              <option value="gaz">Gaz conservé</option>
              <option value="fioul">Fioul conservé</option>
              <option value="elec">Électrique</option>
            </select>

            <label htmlFor="menage">Ménage</label>
            <select
              id="menage"
              value={menage}
              onChange={(e) => setMenage(e.target.value)}
              data-testid="bar168-menage-select"
            >
              <option value="autre">Autre (×4)</option>
              <option value="modeste">Modeste II ter (×5)</option>
            </select>

            <label htmlFor="date">Engagement</label>
            <input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="bar168-date-input"
            />

            <label htmlFor="prix">€ / MWhc</label>
            <input
              id="prix"
              type="number"
              min="0"
              step="0.5"
              value={prix}
              onChange={(e) => setPrix(e.target.value)}
              data-testid="bar168-prix-input"
            />

            <button
              type="button"
              className="btn-calc"
              onClick={runCalc}
              data-testid="bar168-calc-btn"
            >
              Calculer la prime
            </button>
            <p className="hint">
              Rien ne s’affiche à droite tant que vous n’avez pas cliqué.
            </p>
          </form>

          {/* --- Résultats --- */}
          <div>
            <section
              className={"card" + (flash ? " flash" : "")}
              data-testid="bar168-result-card"
            >
              {!calculated ? (
                <p className="placeholder">
                  Saisis le dossier à gauche puis clique <strong>Calculer la prime</strong>.
                </p>
              ) : (
                <>
                  <h2>
                    {usage === "CH" ? "SSC · Chauffage + ECS" : "SSC · ECS seule"} · Zone {s.zone}
                  </h2>
                  <div className="kpis">
                    <div className="kpi">
                      <div className="k">Panneaux</div>
                      <div className="v">{s.n}</div>
                      <div className="u">{s.pose}</div>
                    </div>
                    <div className="kpi">
                      <div className="k">S CEE</div>
                      <div className="v">{fmt(s.S)} m²</div>
                      <div className="u">
                        {s.n} × {s.unit} m² (paramètre)
                      </div>
                    </div>
                    <div className="kpi">
                      <div className="k">Forfait</div>
                      <div className="v">{fmt0(s.c.forfait)}</div>
                      <div className="u">kWhc/m²</div>
                    </div>
                    <div className="kpi">
                      <div className="k">Q base</div>
                      <div className="v">{fmt(s.c.qBase / 1000)}</div>
                      <div className="u">MWhc</div>
                    </div>
                    <div className="kpi">
                      <div className="k">Q total</div>
                      <div className="v">{fmt(s.c.qTot / 1000)}</div>
                      <div className="u">
                        ×{s.c.b.k} · {s.c.b.why}
                      </div>
                    </div>
                    <div className="kpi span" data-testid="bar168-prime-kpi">
                      <div className="k">Prime estimée</div>
                      <div className="v">{euro(s.c.prime)}</div>
                      <div className="u">
                        {fmt(s.S)} m² × {fmt0(s.c.forfait)} × {s.c.b.k} /1000 × {fmt(s.prix)} €
                      </div>
                    </div>
                  </div>
                  <div>
                    {flags.map(([k, t], i) => (
                      <span className={"flag " + k} key={i}>
                        {t}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </section>

            {calculated && (
              <section className="card" style={{ marginTop: 12 }}>
                {usage === "CH" ? (
                  <>
                    <h2>Fourchette auditeur</h2>
                    <table>
                      <thead>
                        <tr>
                          <th>Règle</th>
                          <th className="num">Panneaux</th>
                          <th className="num">S m²</th>
                          <th className="num">Q total</th>
                          <th className="num">Prime</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["10 %", 0.1],
                          ["12,5 %", 0.125],
                          ["15 %", 0.15],
                        ].map(([lab, r]) => {
                          const nX = nFromShab(s.shab, r);
                          const sX = nX * s.unit;
                          const x = pack(
                            sX,
                            "CH",
                            s.zone,
                            s.appoint,
                            s.menage,
                            s.dateStr,
                            s.prix
                          );
                          return (
                            <tr key={lab} className={nX === s.n ? "on" : ""}>
                              <td>{lab}</td>
                              <td className="num">{nX}</td>
                              <td className="num">{fmt(sX)}</td>
                              <td className="num">{fmt(x.qTot / 1000)}</td>
                              <td className="num">{euro(x.prime)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <>
                    <h2>Paliers SSC – ECS seule</h2>
                    <table>
                      <thead>
                        <tr>
                          <th>Foyer</th>
                          <th className="num">Panneaux</th>
                          <th className="num">S m²</th>
                          <th className="num">Prime</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[2, 4, 6].map((p) => {
                          const nX = PARAMS.paliersECS[p];
                          const sX = nX * s.unit;
                          const x = pack(
                            sX,
                            "ECS",
                            s.zone,
                            s.appoint,
                            s.menage,
                            s.dateStr,
                            s.prix
                          );
                          return (
                            <tr key={p} className={p === Number(s.pers) ? "on" : ""}>
                              <td>{p} pers.</td>
                              <td className="num">{nX}</td>
                              <td className="num">{fmt(sX)}</td>
                              <td className="num">{euro(x.prime)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )}
              </section>
            )}
          </div>
        </div>

        <footer>
          Paramètre interne : 2 m² par panneau. Q = n × 2 × forfait 168 × k.
          Non contractuel. 17/09/2026.
        </footer>
      </div>

      <PdfPreviewModal
        open={pdfModal.open}
        previewUrl={pdfModal.previewUrl}
        downloadUrl={pdfModal.downloadUrl}
        filename={pdfModal.filename}
        onClose={() =>
          setPdfModal({ open: false, previewUrl: null, downloadUrl: null, filename: null })
        }
      />

      {/* --- Modal d'auth (mêmes visuels que Calculator.jsx) --- */}
      {authPromptOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center px-4"
          onClick={() => setAuthPromptOpen(null)}
          data-testid="bar168-auth-prompt-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7"
            onClick={(e) => e.stopPropagation()}
            data-testid="bar168-auth-prompt-modal"
          >
            <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold text-slate-900">
              {authPromptOpen === "save"
                ? "Enregistrez cette simulation en vous connectant"
                : "Connectez-vous pour exporter le PDF"}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Le simulateur reste libre d'utilisation. La création d'un compte
              (1&nbsp;clic via Google) vous permet de retrouver vos simulations
              et générer des documents à joindre à vos dossiers CEE.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => {
                  saveDraft({
                    intent: authPromptOpen,
                    kind: "bar168",
                    inputs: {
                      usage,
                      shab,
                      ratio,
                      pers,
                      np,
                      zone,
                      appoint,
                      menage,
                      date,
                      prix,
                      projectName,
                    },
                  });
                  loginWithGoogle("/168");
                }}
                className="flex-1 bg-[#3b82f6] hover:bg-blue-600 text-white rounded-xl h-11 inline-flex items-center justify-center gap-2 font-medium"
                data-testid="bar168-auth-prompt-login-btn"
              >
                <LogIn className="h-4 w-4" />
                Continuer avec Google
              </button>
              <button
                onClick={() => setAuthPromptOpen(null)}
                className="rounded-xl h-11 border border-slate-200 px-4 text-slate-700 hover:bg-slate-50"
                data-testid="bar168-auth-prompt-cancel-btn"
              >
                Plus tard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- CSS scopé (palette GSCE bleue) ----------
const styles = `
.bar168-root{
  --ink:#0f172a;
  --muted:#64748b;
  --line:#e2e8f0;
  --paper:#f4f7fb;
  --card:#ffffff;
  --pine:#1e3a72;
  --brand:#1e3a72;
  --plug:#2563eb;
  --ok:#0f766e;
  --warn:#b45309;
  --bad:#b91c1c;
  --info:#0369a1;
  --mono:"IBM Plex Mono",ui-monospace,monospace;
  background:var(--paper);
  color:var(--ink);
  min-height:calc(100vh - 64px);
}
.bar168-root .wrap{max-width:1080px;margin:0 auto;padding:22px 18px 72px}
.bar168-root .hero{
  background:linear-gradient(135deg,#eef4fb 0%,#dbeafe 100%);
  color:#12233f;border-radius:20px;padding:22px 24px;margin-bottom:16px;
  border:1px solid #c7dbf3;
}
.bar168-root .hero h1{margin:0;font-size:1.6rem;color:#1e3a72;font-weight:700;letter-spacing:-0.01em}
.bar168-root .hero p{margin:8px 0 0;color:#3d4f6a;line-height:1.45;max-width:72ch}
.bar168-root .hero-badge{
  display:inline-block;font-size:.7rem;letter-spacing:.15em;text-transform:uppercase;
  color:#1e3a72;background:#ffffff;border:1px solid #c7dbf3;padding:4px 10px;
  border-radius:999px;font-weight:700;margin-bottom:10px;
}
.bar168-root .hero-actions{display:flex;gap:8px;flex-wrap:wrap;margin-top:14px}
.bar168-root .btn{
  height:40px;padding:0 14px;border-radius:10px;border:0;
  background:#1e3a72;color:#fff;font-weight:700;cursor:pointer;
  display:inline-flex;align-items:center;gap:8px;font-size:.9rem;
  transition:background .2s ease;
}
.bar168-root .btn:hover{background:#152a55}
.bar168-root .btn.ghost{background:transparent;color:#1e3a72;border:1px solid #1e3a72}
.bar168-root .btn.ghost:hover{background:#e0ebf9}
.bar168-root .btn:disabled{opacity:.6;cursor:not-allowed}
.bar168-root .btn-calc{
  width:100%;height:48px;margin-top:16px;background:#2563eb;color:#fff;border:0;
  border-radius:12px;font-weight:800;font-size:1.05rem;cursor:pointer;
  transition:background .2s ease;
}
.bar168-root .btn-calc:hover{background:#1d4ed8}
.bar168-root .btn-calc:active{transform:translateY(1px)}
.bar168-root .placeholder{color:#64748b;font-size:.92rem;line-height:1.45;padding:12px 0}
.bar168-root .flash{animation:bar168flash .7s ease}
@keyframes bar168flash{
  from{box-shadow:0 0 0 0 rgba(30,58,114,.35)}
  to{box-shadow:0 0 0 12px rgba(30,58,114,0)}
}
.bar168-root .modes{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:16px 0}
@media(max-width:640px){.bar168-root .modes{grid-template-columns:1fr}}
.bar168-root .mode{
  border:2px solid var(--line);background:var(--card);
  border-radius:14px;padding:14px;cursor:pointer;text-align:left;color:inherit;font:inherit;
  transition:border-color .2s ease,background .2s ease;
}
.bar168-root .mode:hover{border-color:#93b4d9}
.bar168-root .mode strong{display:block;font-size:.95rem}
.bar168-root .mode span{display:block;font-size:.78rem;color:var(--muted);margin-top:4px;line-height:1.35}
.bar168-root .mode.on{border-color:var(--pine);background:#eff6ff}
.bar168-root .layout{display:grid;grid-template-columns:340px 1fr;gap:14px}
@media(max-width:880px){.bar168-root .layout{grid-template-columns:1fr}}
.bar168-root .card{background:var(--card);border:1px solid var(--line);border-radius:16px;padding:16px 18px}
.bar168-root h2{margin:0 0 10px;font-size:1.05rem;color:#1e3a72;font-weight:700}
.bar168-root label{display:block;font-size:.8rem;font-weight:650;margin:10px 0 5px;color:#334155}
.bar168-root select,
.bar168-root input{
  width:100%;height:42px;border:1px solid #cbd5e1;border-radius:10px;
  padding:0 11px;font:inherit;background:#fff;color:var(--ink);
  transition:border-color .15s ease,box-shadow .15s ease;
}
.bar168-root select:focus,
.bar168-root input:focus{outline:0;border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.15)}
.bar168-root .hint{font-size:.76rem;color:var(--muted);line-height:1.4;margin:4px 0 0}
.bar168-root .kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
@media(max-width:700px){.bar168-root .kpis{grid-template-columns:1fr}}
.bar168-root .kpi{background:#eff6ff;border:1px solid #dbeafe;border-radius:11px;padding:11px}
.bar168-root .kpi.span{grid-column:1/-1;background:linear-gradient(135deg,#1e3a72,#2563eb);color:#f1f5ff;border:0}
.bar168-root .k{font-size:.66rem;letter-spacing:.05em;text-transform:uppercase;color:var(--muted)}
.bar168-root .kpi.span .k{color:#c7dbf3}
.bar168-root .v{font-family:var(--mono);font-size:1.35rem;font-weight:700;margin-top:2px}
.bar168-root .u{font-size:.76rem;opacity:.9}
.bar168-root table{width:100%;border-collapse:collapse;font-size:.85rem;margin-top:8px}
.bar168-root th,
.bar168-root td{padding:7px 6px;border-bottom:1px solid var(--line);text-align:left}
.bar168-root th{font-size:.66rem;text-transform:uppercase;letter-spacing:.04em;color:var(--muted)}
.bar168-root .num{text-align:right;font-family:var(--mono)}
.bar168-root tr.on{background:#eff6ff}
.bar168-root .flag{
  display:inline-block;font-size:.72rem;border-radius:999px;
  padding:3px 8px;margin:8px 4px 0 0;border:1px solid var(--line);
}
.bar168-root .ok{background:#e7f9f2;color:var(--ok);border-color:#bbf0dd}
.bar168-root .warn{background:#fff7ed;color:var(--warn);border-color:#fed7aa}
.bar168-root .bad{background:#fef2f2;color:var(--bad);border-color:#fecaca}
.bar168-root .info{background:#e0f2fe;color:var(--info);border-color:#bae6fd}
.bar168-root footer{margin-top:16px;font-size:.74rem;color:var(--muted);line-height:1.5}
`;
